import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { LogMessage } from "@v2/features/page-renderer/hooks/useNextDevServerHook";

import * as React from "react";
import { useRef } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTrigger,
} from "@replo/design-system/components/combobox/Combobox";
import { toast } from "@replo/design-system/components/toast/Toast";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { PanelBottom, PanelLeft, PanelRight, PanelTop, X } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

import { useNextDevServer } from "./context";

const formatLogMessage = (log: LogMessage) => {
  return `${log.date.toLocaleTimeString()} ${log.message}`;
};

type Position = "left" | "right" | "bottom" | "top";

type WebContainerLogsProps = {
  onClose?: () => void;
};

const getPositionClasses = (position: Position) => {
  return exhaustiveSwitch({ type: position })({
    bottom: "fixed bottom-0 left-0 right-0",
    top: "fixed top-0 left-0 right-0",
    left: "fixed top-0 bottom-0 left-0",
    right: "fixed top-0 bottom-0 right-0",
  });
};

const getPositionIcon = (position: Position) => {
  return exhaustiveSwitch({ type: position })({
    bottom: <PanelBottom size={16} />,
    top: <PanelTop size={16} />,
    left: <PanelLeft size={16} />,
    right: <PanelRight size={16} />,
  });
};

const WebContainerLogs = ({ onClose }: WebContainerLogsProps) => {
  const { logs } = useNextDevServer();
  const [position, setPosition] = useLocalStorageState(
    "replo:debug:code-execution-logs-position",
    "bottom" as Position,
    { schema: z.enum(["left", "right", "bottom", "top"]) },
  );

  const logsRef = useRef<HTMLPreElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to scroll to the bottom of the logs when they change
  React.useEffect(() => {
    if (logsRef.current) {
      logsRef.current?.scrollTo({
        top: logsRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [logs]);

  const positionItems: ComboboxItemWithValue[] = [
    {
      value: "bottom",
      label: "Bottom",
      startEnhancer: <PanelBottom size={16} />,
    },
    {
      value: "top",
      label: "Top",
      startEnhancer: <PanelTop size={16} />,
    },
    {
      value: "left",
      label: "Left",
      startEnhancer: <PanelLeft size={16} />,
    },
    {
      value: "right",
      label: "Right",
      startEnhancer: <PanelRight size={16} />,
    },
  ];

  return (
    <div
      className={twMerge(
        getPositionClasses(position),
        "z-50 bg-white flex flex-col border-1",
      )}
    >
      <div className="absolute right-5.5 top-2 flex items-center gap-2">
        <ComboboxRoot
          items={positionItems}
          value={positionItems.find((item) => item.value === position) ?? null}
          onValueChange={(item) => {
            if (item) {
              setPosition(item.value as Position);
            }
          }}
          itemToStringValue={(item) => String(item.label)}
          isItemEqualToValue={(a, b) => a.value === b.value}
          filter={null}
        >
          <Tooltip content="Change position" triggerAsChild>
            <ComboboxTrigger
              render={
                <Button icon variant="secondary" size="sm">
                  {getPositionIcon(position)}
                </Button>
              }
            />
          </Tooltip>
          <ComboboxContent UNSAFE_className="w-[125px]">
            <ComboboxList>
              {(item: ComboboxItemWithValue) => (
                <ComboboxItem key={item.value} {...item} value={item} />
              )}
            </ComboboxList>
          </ComboboxContent>
        </ComboboxRoot>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void navigator.clipboard
              .writeText(
                logs.map(formatLogMessage).join("\n").replaceAll(/\n+/g, "\n"),
              )
              .then(() => {
                toast({ title: "Logs copied to clipboard" });
              });
          }}
        >
          Copy Logs
        </Button>
        {onClose && (
          <Tooltip content="Close Logs" triggerAsChild>
            <Button icon variant="secondary" size="sm" onClick={onClose}>
              <X />
            </Button>
          </Tooltip>
        )}
      </div>
      <pre
        ref={logsRef}
        className={twMerge(
          // eslint-disable-next-line replo/no-tailwind-literal-color
          "overflow-x-auto overflow-y-auto bg-black p-2 text-2xs text-green-400",
          position === "left" && "w-96 h-full",
          position === "right" && "w-96 h-full",
          position === "top" && "h-96",
          position === "bottom" && "h-96",
        )}
      >
        {logs.map((log, index) => (
          <div key={index}>{formatLogMessage(log)}</div>
        ))}
      </pre>
    </div>
  );
};

export default WebContainerLogs;
